import { useEffect, useRef } from "react";
import { useApiIsLoaded, useMap } from "@vis.gl/react-google-maps";
import {
    TerraDraw,
    TerraDrawPolygonMode,
    TerraDrawCircleMode,
    TerraDrawRectangleMode,
} from "terra-draw";
import { TerraDrawGoogleMapsAdapter } from "terra-draw-google-maps-adapter";


/**
 * TerraDrawOverlay - wraps TerraDraw for Google Maps.
 */
export const TerraDrawOverlay = ({ color, drawingMode, onShapeComplete }) => {
    const map = useMap();
    const apiIsLoaded = useApiIsLoaded();
    const drawRef = useRef(null);

    useEffect(() => {
        if (!apiIsLoaded || !map) return;

        const mapsLib = window.google?.maps;
        if (!mapsLib?.OverlayView) {
            console.warn("Google Maps OverlayView is not available yet");
            return;
        }

        let projectionListener = null;
        let isCancelled = false;

        const startTerraDraw = () => {
            if (isCancelled || drawRef.current) return;

            try {
                const draw = new TerraDraw({
                    adapter: new TerraDrawGoogleMapsAdapter({
                        map,
                        lib: google.maps,
                        coordinatePrecision: 9,
                    }),

                    modes: [
                        new TerraDrawPolygonMode({
                            styles: {
                                fillColor: color,
                                outlineColor: color,
                            },
                        }),

                        new TerraDrawRectangleMode({
                            styles: {
                                fillColor: color,
                                outlineColor: color,
                            },
                        }),

                        new TerraDrawCircleMode({
                            styles: {
                                fillColor: color,
                                outlineColor: color,
                            },
                        }),
                    ],
                });

                drawRef.current = draw;

                draw.start();

                draw.on("ready", () => {
                    console.log('TerraDraw is ready');
                    draw.setMode(drawingMode ?? 'static');
                });

                const handleFinish = (feature_id) => {
                    const snapshot = draw.getSnapshot();
                    const feature = snapshot.find((f) => f.id === feature_id);
                    if (feature) onShapeComplete(feature);
                };

                draw.on('finish', handleFinish);

            } catch (e) {
                console.error("TerraDraw start failed", e);
            }
        };
        if (map.getProjection?.()) {
            startTerraDraw();
        } else {
            projectionListener = mapsLib.event.addListenerOnce(
                map,
                "projection_changed",
                startTerraDraw
            );
        }

        return () => {
            isCancelled = true;
            projectionListener?.remove();

            if (drawRef.current) {
                drawRef.current.stop();
                drawRef.current = null;
            }
        };
    }, [map]);

    useEffect(() => {
        if (!drawRef.current || !drawingMode) return;
        if(drawingMode === 'static'){
            drawRef.current.clear();
        }
        drawRef.current.setMode(drawingMode);
    }, [drawingMode]);

    return null;
};
